import Debug from 'debug';
import React, { createContext, useEffect, useReducer, useRef, useState } from 'react';
import { getSettingsService } from '../services/SettingsService';

const debug = Debug('o365:overlay-coordinator');

/** Actions that can be performed by the reducer. */
export type OverlayCoordinatorAction = 'dismissInfoOverlay' | 'showInfoOverlay' | 'videoInsert';

/** Helper type definition for the dispatch function. */
export type OverlayCoordinatorDispatch = React.Dispatch<OverlayCoordinatorAction>;

/** Context object holding the dispatch method for the coordinator. */
export const OverlayCoordinatorContext = createContext<OverlayCoordinatorDispatch>(() => {
  /* deliberate */
});

/** Initial state for the overlay. */
const overlayInitialState = Object.freeze({
  /* Used to indicate the overlay has been dismissed. */
  overlayDismissed: false,
  /* Used to show the info overlay. */
  showInfoOverlay: false,
  /* Used to indicate when a video has been inserted. */
  videoInserted: false,
});

/** Describes the state returned by the overlay coordinator reducer. */
export type OverlayCoordinatorState = Record<keyof typeof overlayInitialState, boolean>;

/**
 * Converts the given action and current state in to a new state.
 * @arg state: The current state.
 * @arg action: The action to perform on the state.
 * @returns A new state object based on the action, or the original object if no change was necessary.
 */
function overlayCoordinatorReducer(
  state: OverlayCoordinatorState,
  action: OverlayCoordinatorAction,
): OverlayCoordinatorState {
  switch (action) {
    case 'dismissInfoOverlay':
      return { ...state, showInfoOverlay: false, overlayDismissed: true };
    case 'showInfoOverlay':
      return { ...state, showInfoOverlay: true };
    case 'videoInsert':
      return { ...state, videoInserted: true };
  }
}

/**
 * Provides a hook to set up the reducer and state as well as manage the reactions to the state as needed.
 * @returns an array matching the typical @see useReducer function, containing the current state as the
 * first item and the dispatcher as the second item.
 */
export function useOverlayCoordinator(): [OverlayCoordinatorState, OverlayCoordinatorDispatch] {
  const [state, dispatch] = useReducer(overlayCoordinatorReducer, overlayInitialState);
  const checkedInitialState = useRef(false);
  const [canLoadSettings, setCanLoadSettings] = useState(false);

  useEffect(() => {
    Office.onReady(() => setCanLoadSettings(true));
  }, []);

  useEffect(() => {
    // skip loading until office is ready.
    if (!canLoadSettings) {
      return;
    }

    const settings = getSettingsService('videoStats');

    // check the state from the settings and see if we need to show the overlay.
    if (!checkedInitialState.current) {
      debug('setting reducer initial state from settings');
      const hasShownSendAndTrackInfo = settings.get('hasShownSendAndTrackInfo');
      const hasDismissedSendAndTrackInfo = settings.get('hasDismissedSendAndTrackInfo');

      if (hasShownSendAndTrackInfo && !hasDismissedSendAndTrackInfo) {
        debug('user has seen the info window but not dismissed it, setting up to display');
        dispatch('showInfoOverlay');
      }

      debug('settings as known', { hasShownSendAndTrackInfo, hasDismissedSendAndTrackInfo });
      checkedInitialState.current = true;
      return;
    }

    // if a video was inserted and we have not shown the overlay yet, show it now.
    if (state.videoInserted && !settings.get('hasShownSendAndTrackInfo')) {
      debug('showing info overlay for the first time');
      settings.set('hasShownSendAndTrackInfo', true);
      dispatch('showInfoOverlay');
      return;
    }

    // if the user has dismissed the info overlay, record it so they never see it again.
    if (state.overlayDismissed) {
      debug('user dismissed overlay, setting flag to never show again');
      settings.set('hasDismissedSendAndTrackInfo', true);
      return;
    }
  }, [state, canLoadSettings]);

  return [state, dispatch];
}
